/**
 * NovaChatWidget — the main `<nova-chat>` Web Component.
 *
 * Renders a slide-out Help Center panel with a collapsible
 * "My Submitted Issues" accordion, topic quick-pick chips,
 * typing simulation, file attachments, updates banner, and
 * full WCAG 2.1 AA accessibility including focus trap and
 * keyboard navigation.
 */

import { WidgetApiClient } from './api-client.js';
import { injectTriggerButton } from './trigger-injector.js';
import { I18n } from './i18n/index.js';
import { getWidgetStyles, createStyleElement } from './styles.js';
import { createPrivacyNotice, isPrivacyDismissed } from './views/privacy-notice.js';
import { setupRichMedia, createAttachmentPreviews } from './rich-media.js';
import type { FileAttachment } from './rich-media.js';
import type {
  ChatMessage,
  ChatResponse,
  ChatbotConfigResponse,
  UserTicket,
  TicketUpdate,
} from './types.js';

/* ------------------------------------------------------------------ */
/*  Config                                                             */
/* ------------------------------------------------------------------ */

export interface WidgetTheme {
  brandColor?: string;
  backgroundColor?: string;
  textColor?: string;
  fontFamily?: string;
  borderRadius?: string;
  darkMode?: boolean | 'auto';
}

export interface WidgetConfig {
  apiUrl: string;
  apiKey: string;
  apiSecret: string;
  appId: string;
  externalUserId: string;
  externalUserDisplayName?: string;
  triggerSelector?: string;
  triggerContainer?: string;
  triggerLabel?: string;
  theme?: WidgetTheme;
  emitter?: { emit: (event: string, data: unknown) => void };
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STORAGE_LAST_SEEN = 'nova_last_seen';
const TYPING_SPEED_MIN_MS = 15;
const TYPING_SPEED_MAX_MS = 30;

/* ------------------------------------------------------------------ */
/*  NovaChatWidget                                                     */
/* ------------------------------------------------------------------ */

export class NovaChatWidget {
  private readonly config: WidgetConfig;
  private readonly api: WidgetApiClient;
  private i18n: I18n;
  private readonly host: HTMLElement;
  private readonly shadow: ShadowRoot;
  private readonly panel: HTMLElement;
  private readonly backdrop: HTMLElement;

  // DOM references
  private messagesContainer!: HTMLElement;
  private inputArea!: HTMLElement;
  private textarea!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private updatesBanner!: HTMLElement;
  private privacySlot!: HTMLElement;
  private issuesAccordion!: HTMLElement;
  private issuesListEl!: HTMLElement;
  private issuesChevron!: HTMLElement;
  private issuesCountEl!: HTMLElement;

  // State
  private isOpen = false;
  private issuesExpanded = false;
  private issueCount = 0;
  private messages: ChatMessage[] = [];
  private sessionId: string | undefined;
  private readonly sessionStorageKey: string;
  private pendingAttachments: FileAttachment[] = [];
  private isSending = false;
  private chatbotConfig: ChatbotConfigResponse | undefined;
  private triggerElement: Element | null = null;
  private previousFocusElement: Element | null = null;
  private destroyed = false;
  private badgeEl: HTMLElement | null = null;
  private unreadInterval: ReturnType<typeof setInterval> | null = null;
  private styleEl: HTMLElement;

  private darkModeObserver: MutationObserver | null = null;
  private darkModeMediaQuery: MediaQueryList | null = null;
  private darkModeMediaHandler: ((e: MediaQueryListEvent) => void) | null = null;
  private triggerCleanup?: () => void;

  // Bound handlers for cleanup
  private readonly boundKeydownHandler: (e: KeyboardEvent) => void;
  private readonly boundTriggerHandler: () => void;

  /**
   * AbortController for all shadow DOM event listeners.
   * Calling abort() in destroy() removes every listener
   * that was registered with this controller's signal,
   * preventing leaks on SPA renavigation.
   */
  private readonly listenerAbort: AbortController;

  constructor(config: WidgetConfig) {
    this.validateConfig(config);
    this.config = config;

    // Persist sessionId across page navigations so the AI retains conversation context
    this.sessionStorageKey = `nova_session_${config.appId}_${config.externalUserId}`;
    try {
      this.sessionId = localStorage.getItem(this.sessionStorageKey) ?? undefined;
    } catch {
      // localStorage may be blocked (incognito, CSP) — degrade gracefully
    }

    this.api = new WidgetApiClient({
      apiUrl: config.apiUrl,
      apiKey: config.apiKey,
      apiSecret: config.apiSecret,
      appId: config.appId,
      externalUserId: config.externalUserId,
      externalUserDisplayName: config.externalUserDisplayName,
    });

    this.i18n = new I18n();
    this.listenerAbort = new AbortController();

    // Create shadow host
    this.host = document.createElement('div');
    this.host.setAttribute('data-nova-widget', 'true');
    this.shadow = this.host.attachShadow({ mode: 'open' });

    // Inject styles — detect host page dark mode for initial render
    const initDarkMode = config.theme?.darkMode ?? 'auto';
    const effectiveInitDark = initDarkMode === 'auto' ? this.detectHostDarkMode() : initDarkMode;
    const styles = getWidgetStyles({ darkMode: effectiveInitDark });
    this.styleEl = createStyleElement(styles);
    this.shadow.appendChild(this.styleEl);

    // Build backdrop (click-outside-to-close)
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'nova-backdrop';
    this.backdrop.addEventListener('click', () => this.close(), { signal: this.listenerAbort.signal });
    this.shadow.appendChild(this.backdrop);

    // Build panel
    this.panel = this.buildPanel();
    this.shadow.appendChild(this.panel);

    // Read design tokens from host page CSS custom properties (base layer)
    const hostTokens = this.readHostTokens();
    if (Object.keys(hostTokens).length > 0) {
      this.applyTheme(hostTokens);
    }

    // Apply init-provided theme on top (overrides host tokens)
    if (config.theme) {
      this.applyTheme(config.theme);
    }

    // Append to body
    document.body.appendChild(this.host);

    // Bind event handlers (store references for cleanup)
    this.boundKeydownHandler = (e: KeyboardEvent) => this.handleKeydown(e);
    this.boundTriggerHandler = () => this.toggle();

    // Global keydown for Escape
    document.addEventListener('keydown', this.boundKeydownHandler);

    // Bind trigger
    if (config.triggerSelector) {
      this.triggerElement = document.querySelector(config.triggerSelector);
      if (this.triggerElement) {
        this.triggerElement.addEventListener('click', this.boundTriggerHandler);
      }
    }

    // Inject navbar trigger button — uses explicit selector if provided,
    // otherwise auto-detects a suitable nav/header container.
    this.triggerCleanup = injectTriggerButton(
      config.triggerContainer ?? null,
      config.triggerLabel ?? 'AI Assistant',
      () => this.toggle(),
    );

    // Unread notification badge
    this.badgeEl = document.createElement('div');
    this.badgeEl.setAttribute('aria-label', 'New updates on your issues');
    this.badgeEl.style.cssText = 'display:none;position:absolute;top:-2px;right:-2px;width:10px;height:10px;border-radius:50%;background:#ef4444;border:2px solid white;pointer-events:none;';
    if (this.triggerElement) {
      (this.triggerElement as HTMLElement).style.position = 'relative';
      this.triggerElement.appendChild(this.badgeEl);
    }

    // Fetch config from API (non-blocking — server values fill gaps
    // but do not overwrite init-provided theme tokens)
    void this.loadConfig();

    // Watch host page for dark mode changes (Tailwind class, data-theme, etc.)
    if (initDarkMode === 'auto') {
      this.watchHostDarkMode();
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Public API                                                       */
  /* ---------------------------------------------------------------- */

  open(): void {
    if (this.destroyed || this.isOpen) return;
    this.isOpen = true;
    this.previousFocusElement = document.activeElement;
    this.panel.classList.add('nova-panel--open');
    this.panel.setAttribute('aria-hidden', 'false');
    this.backdrop.classList.add('nova-backdrop--visible');

    // Show privacy notice on first open
    if (!isPrivacyDismissed() && this.privacySlot.childNodes.length === 0) {
      const notice = createPrivacyNotice(this.i18n.t('privacy_notice'), () => {
        // no-op callback; notice removes itself
      });
      this.privacySlot.appendChild(notice);
    }

    // Focus the textarea
    requestAnimationFrame(() => {
      this.textarea.focus();
    });

    // Check for updates and load issues
    void this.checkUpdates();
    void this.loadIssues();

    this.emitEvent('widget:opened', {});
  }

  close(): void {
    if (this.destroyed || !this.isOpen) return;
    this.isOpen = false;
    this.panel.classList.remove('nova-panel--open');
    this.panel.setAttribute('aria-hidden', 'true');
    this.backdrop.classList.remove('nova-backdrop--visible');

    // Return focus to trigger or previous element
    const returnTarget = this.triggerElement ?? this.previousFocusElement;
    if (returnTarget && 'focus' in returnTarget) {
      (returnTarget as HTMLElement).focus();
    }

    this.emitEvent('widget:closed', {});
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    // Remove document-level and trigger listeners (registered without AbortController)
    document.removeEventListener('keydown', this.boundKeydownHandler);

    if (this.triggerElement) {
      this.triggerElement.removeEventListener('click', this.boundTriggerHandler);
    }

    // Abort all shadow DOM listeners in one call
    this.listenerAbort.abort();

    if (this.unreadInterval) {
      clearInterval(this.unreadInterval);
    }

    if (this.darkModeObserver) {
      this.darkModeObserver.disconnect();
    }
    if (this.darkModeMediaQuery && this.darkModeMediaHandler) {
      this.darkModeMediaQuery.removeEventListener('change', this.darkModeMediaHandler);
    }

    this.triggerCleanup?.();

    this.host.remove();
  }

  /* ---------------------------------------------------------------- */
  /*  Config validation                                                */
  /* ---------------------------------------------------------------- */

  private validateConfig(config: WidgetConfig): void {
    if (!config.apiUrl) throw new Error('NovaChatWidget: apiUrl is required');
    if (!config.apiKey) throw new Error('NovaChatWidget: apiKey is required');
    if (!config.apiSecret) throw new Error('NovaChatWidget: apiSecret is required');
    if (!config.appId) throw new Error('NovaChatWidget: appId is required');
    if (!config.externalUserId) throw new Error('NovaChatWidget: externalUserId is required');
  }

  /* ---------------------------------------------------------------- */
  /*  Panel construction                                               */
  /* ---------------------------------------------------------------- */

  private buildPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'nova-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Help Center');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-hidden', 'true');

    // Header
    panel.appendChild(this.buildHeader());

    // Updates banner (hidden by default)
    this.updatesBanner = document.createElement('div');
    this.updatesBanner.className = 'nova-updates-banner';
    this.updatesBanner.style.display = 'none';
    panel.appendChild(this.updatesBanner);

    // Privacy notice slot
    this.privacySlot = document.createElement('div');
    panel.appendChild(this.privacySlot);

    // Issues accordion (collapsible, starts collapsed)
    this.issuesAccordion = this.buildIssuesAccordion();
    panel.appendChild(this.issuesAccordion);

    // Messages container (chat messages — always visible, scrollable, flex:1)
    this.messagesContainer = document.createElement('div');
    this.messagesContainer.className = 'nova-messages';
    this.messagesContainer.setAttribute('aria-live', 'polite');
    this.messagesContainer.setAttribute('aria-relevant', 'additions');
    panel.appendChild(this.messagesContainer);

    // Input area (always visible)
    this.inputArea = this.buildInputArea();
    panel.appendChild(this.inputArea);

    // Add welcome message
    this.addAssistantMessage(this.i18n.t('welcome_default'));

    return panel;
  }

  private buildHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'nova-header';

    const iconWrap = document.createElement('span');
    iconWrap.className = 'nova-header__icon';
    iconWrap.setAttribute('aria-hidden', 'true');
    iconWrap.innerHTML = this.novaSparklesSvg();
    header.appendChild(iconWrap);

    const title = document.createElement('span');
    title.className = 'nova-header__title';
    title.textContent = 'Help Center';
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'nova-header__close';
    closeBtn.setAttribute('aria-label', this.i18n.t('close'));
    closeBtn.setAttribute('type', 'button');
    closeBtn.textContent = '\u00D7';
    closeBtn.addEventListener('click', () => this.close(), { signal: this.listenerAbort.signal });
    header.appendChild(closeBtn);

    return header;
  }

  private buildIssuesAccordion(): HTMLElement {
    const accordion = document.createElement('div');
    accordion.className = 'nova-issues-accordion';

    // Header button (clickable to expand/collapse)
    const headerBtn = document.createElement('button');
    headerBtn.className = 'nova-issues-accordion__header';
    headerBtn.setAttribute('type', 'button');
    headerBtn.setAttribute('aria-expanded', 'false');
    headerBtn.setAttribute('aria-controls', 'nova-issues-list');

    const clipboardIcon = document.createElement('span');
    clipboardIcon.className = 'nova-issues-accordion__icon';
    clipboardIcon.setAttribute('aria-hidden', 'true');
    clipboardIcon.innerHTML = this.clipboardSvg();

    const titleSpan = document.createElement('span');
    titleSpan.className = 'nova-issues-accordion__title';
    titleSpan.textContent = this.i18n.t('my_submitted_issues');

    this.issuesCountEl = document.createElement('span');
    this.issuesCountEl.className = 'nova-issues-accordion__count';
    this.issuesCountEl.textContent = '0';

    this.issuesChevron = document.createElement('span');
    this.issuesChevron.className = 'nova-issues-accordion__chevron';
    this.issuesChevron.setAttribute('aria-hidden', 'true');
    this.issuesChevron.textContent = '\u25B8'; // right-pointing triangle

    headerBtn.appendChild(clipboardIcon);
    headerBtn.appendChild(titleSpan);
    headerBtn.appendChild(this.issuesCountEl);
    headerBtn.appendChild(this.issuesChevron);

    headerBtn.addEventListener('click', () => this.toggleIssuesAccordion(headerBtn), { signal: this.listenerAbort.signal });

    // List container (hidden when collapsed)
    this.issuesListEl = document.createElement('div');
    this.issuesListEl.className = 'nova-issues-accordion__list';
    this.issuesListEl.setAttribute('id', 'nova-issues-list');
    this.issuesListEl.setAttribute('role', 'region');
    this.issuesListEl.setAttribute('aria-label', this.i18n.t('my_submitted_issues'));

    accordion.appendChild(headerBtn);
    accordion.appendChild(this.issuesListEl);

    return accordion;
  }

  private toggleIssuesAccordion(headerBtn: HTMLButtonElement): void {
    this.issuesExpanded = !this.issuesExpanded;
    headerBtn.setAttribute('aria-expanded', String(this.issuesExpanded));

    if (this.issuesExpanded) {
      this.issuesListEl.classList.add('nova-issues-accordion__list--expanded');
      this.issuesChevron.classList.add('nova-issues-accordion__chevron--expanded');
      // Mark as seen when expanding issues
      try {
        localStorage.setItem('nova-widget-last-visit', new Date().toISOString());
      } catch { /* localStorage unavailable */ }
      this.updateTriggerBadge(false);
    } else {
      this.issuesListEl.classList.remove('nova-issues-accordion__list--expanded');
      this.issuesChevron.classList.remove('nova-issues-accordion__chevron--expanded');
    }
  }

  private buildInputArea(): HTMLElement {
    const area = document.createElement('div');
    area.className = 'nova-input-area';

    this.textarea = document.createElement('textarea');
    this.textarea.className = 'nova-input';
    this.textarea.setAttribute('placeholder', this.i18n.t('chat_placeholder'));
    this.textarea.setAttribute('rows', '1');
    this.textarea.setAttribute('aria-label', this.i18n.t('chat_placeholder'));
    this.textarea.addEventListener('keydown', (e: Event) => this.handleTextareaKeydown(e as KeyboardEvent), { signal: this.listenerAbort.signal });
    this.textarea.addEventListener('input', () => this.autoResizeTextarea(), { signal: this.listenerAbort.signal });

    const attachBtn = document.createElement('button');
    attachBtn.className = 'nova-attach-btn';
    attachBtn.setAttribute('aria-label', this.i18n.t('attachment_add'));
    attachBtn.setAttribute('type', 'button');
    attachBtn.innerHTML = this.paperclipSvg();

    this.sendBtn = document.createElement('button');
    this.sendBtn.className = 'nova-send-btn';
    this.sendBtn.setAttribute('aria-label', this.i18n.t('chat_send'));
    this.sendBtn.setAttribute('type', 'button');
    this.sendBtn.disabled = true;
    this.sendBtn.innerHTML = this.sendSvg();
    this.sendBtn.addEventListener('click', () => void this.handleSend(), { signal: this.listenerAbort.signal });

    area.appendChild(attachBtn);
    area.appendChild(this.textarea);
    area.appendChild(this.sendBtn);

    // Setup rich media handlers
    setupRichMedia(area, (files: FileAttachment[]) => {
      this.pendingAttachments.push(...files);
      this.renderAttachmentPreviews();
      this.updateSendButton();
    });

    return area;
  }

  /* ---------------------------------------------------------------- */
  /*  (Tabs removed — issues are now in a collapsible accordion)       */
  /* ---------------------------------------------------------------- */

  /* ---------------------------------------------------------------- */
  /*  Message handling                                                 */
  /* ---------------------------------------------------------------- */

  private addUserMessage(content: string): void {
    const msg: ChatMessage = {
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    this.messages.push(msg);
    this.renderMessage(msg);
    this.scrollToBottom();
  }

  private addAssistantMessage(content: string, intent?: ChatMessage['intent']): void {
    const msg: ChatMessage = {
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      intent,
    };
    this.messages.push(msg);
    this.renderMessage(msg);
    this.scrollToBottom();
  }

  private renderMessage(msg: ChatMessage): void {
    const el = document.createElement('div');
    el.className = `nova-message nova-message--${msg.role}`;
    el.textContent = msg.content;
    this.messagesContainer.appendChild(el);
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    });
  }

  private renderSuggestedPrompts(prompts: string[]): void {
    // Remove any existing topic label + chips container
    const existingLabel = this.messagesContainer.querySelector('.nova-topic-label');
    if (existingLabel) existingLabel.remove();
    const existingChips = this.messagesContainer.querySelector('.nova-topic-chips');
    if (existingChips) existingChips.remove();

    if (prompts.length === 0) return;

    // Label
    const label = document.createElement('div');
    label.className = 'nova-topic-label';
    label.textContent = this.i18n.t('pick_topic');
    this.messagesContainer.appendChild(label);

    // Chips container
    const container = document.createElement('div');
    container.className = 'nova-topic-chips';

    for (const prompt of prompts) {
      const btn = document.createElement('button');
      btn.className = 'nova-topic-chip';
      btn.setAttribute('type', 'button');
      btn.textContent = prompt;
      btn.addEventListener('click', () => {
        // Remove the label + chips once one is clicked
        label.remove();
        container.remove();
        // Set the input and send as if the user typed it
        this.textarea.value = prompt;
        this.textarea.dispatchEvent(new Event('input'));
        void this.handleSend();
      }, { signal: this.listenerAbort.signal });
      container.appendChild(btn);
    }

    this.messagesContainer.appendChild(container);
    this.scrollToBottom();
  }

  /* ---------------------------------------------------------------- */
  /*  Typing indicator                                                 */
  /* ---------------------------------------------------------------- */

  private showTypingIndicator(): HTMLElement {
    const indicator = document.createElement('div');
    indicator.className = 'nova-typing';
    indicator.setAttribute('role', 'status');
    indicator.setAttribute('aria-label', this.i18n.t('typing'));

    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('span');
      dot.className = 'nova-typing__dot';
      indicator.appendChild(dot);
    }

    // Screen-reader text
    const srText = document.createElement('span');
    srText.className = 'nova-sr-only';
    srText.textContent = this.i18n.t('typing');
    indicator.appendChild(srText);

    this.messagesContainer.appendChild(indicator);
    this.scrollToBottom();

    return indicator;
  }

  private removeTypingIndicator(indicator: HTMLElement): void {
    indicator.remove();
  }

  /* ---------------------------------------------------------------- */
  /*  Typing simulation                                                */
  /* ---------------------------------------------------------------- */

  private async simulateTyping(text: string, indicator: HTMLElement): Promise<void> {
    this.removeTypingIndicator(indicator);

    const el = document.createElement('div');
    el.className = 'nova-message nova-message--assistant';
    this.messagesContainer.appendChild(el);
    this.scrollToBottom();

    // Check prefers-reduced-motion (guard for environments without matchMedia)
    let prefersReducedMotion = false;
    try {
      prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      // matchMedia not supported — fall back to animated rendering
    }

    if (prefersReducedMotion) {
      el.textContent = text;
      this.scrollToBottom();
      return;
    }

    // Token-by-token rendering
    for (let i = 0; i < text.length; i++) {
      el.textContent = text.slice(0, i + 1);
      this.scrollToBottom();
      const delay =
        TYPING_SPEED_MIN_MS +
        Math.random() * (TYPING_SPEED_MAX_MS - TYPING_SPEED_MIN_MS);
      await this.sleep(delay);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /* ---------------------------------------------------------------- */
  /*  Send message                                                     */
  /* ---------------------------------------------------------------- */

  private async handleSend(): Promise<void> {
    const text = this.textarea.value.trim();
    if ((!text && this.pendingAttachments.length === 0) || this.isSending) return;

    this.isSending = true;
    this.sendBtn.disabled = true;

    // Remove topic chips once user engages
    const topicLabel = this.messagesContainer.querySelector('.nova-topic-label');
    if (topicLabel) topicLabel.remove();
    const topicChips = this.messagesContainer.querySelector('.nova-topic-chips');
    if (topicChips) topicChips.remove();

    // Show user message
    if (text) {
      this.addUserMessage(text);
    }
    if (this.pendingAttachments.length > 0) {
      this.addUserMessage(
        `[${this.pendingAttachments.length} attachment(s)]`,
      );
    }

    // Clear input
    this.textarea.value = '';
    this.autoResizeTextarea();
    const attachments = [...this.pendingAttachments];
    this.pendingAttachments = [];
    this.renderAttachmentPreviews();
    this.resetAttachmentCount();

    // Show typing indicator
    const indicator = this.showTypingIndicator();

    try {
      const response: ChatResponse = await this.api.sendMessage({
        appId: this.config.appId,
        externalUserId: this.config.externalUserId,
        externalUserDisplayName: this.config.externalUserDisplayName,
        message: text,
        sessionId: this.sessionId,
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        pageTitle: typeof document !== 'undefined' ? document.title : undefined,
        pageReferrer: typeof document !== 'undefined' && document.referrer ? document.referrer : undefined,
        attachments: attachments.map((a) => ({
          filename: a.filename,
          contentType: a.contentType,
          data: a.data,
        })),
      });

      this.sessionId = response.sessionId;
      try {
        if (response.sessionId) localStorage.setItem(this.sessionStorageKey, response.sessionId);
      } catch { /* localStorage unavailable */ }

      const assistantContent = response.message?.content ?? '';

      // Simulate typing for response
      await this.simulateTyping(assistantContent, indicator);

      // Store the message in history
      this.messages.push({
        role: 'assistant',
        content: assistantContent,
        timestamp: response.message?.timestamp ?? new Date().toISOString(),
        intent: response.message?.intent,
      });

      // Ticket creation confirmation
      if (response.ticketCreated?.ticketId) {
        const confirmEl = document.createElement('div');
        confirmEl.className = 'nova-message nova-message--system';
        confirmEl.textContent = 'Ticket created — track it in My Submitted Issues above.';
        this.messagesContainer.appendChild(confirmEl);
        this.scrollToBottom();

        this.emitEvent('ticket:created', {
          ticketId: response.ticketCreated.ticketId,
          category: response.ticketCreated.category,
        });
      }

      // Ticket update confirmation (follow-up appended to existing ticket)
      if (response.ticketUpdated?.ticketId) {
        const updateEl = document.createElement('div');
        updateEl.className = 'nova-message nova-message--system';
        updateEl.textContent = 'Your issue has been updated with the latest details.';
        this.messagesContainer.appendChild(updateEl);
        this.scrollToBottom();
      }

      this.emitEvent('message:sent', { sessionId: this.sessionId });
    } catch (err) {
      // Surface the underlying error for diagnosis — silent swallow here
      // previously masked response-shape mismatches for a long time.
      // eslint-disable-next-line no-console
      console.error('[Nova widget] send failed:', err);
      this.removeTypingIndicator(indicator);
      // Show error fallback
      const errorEl = document.createElement('div');
      errorEl.className = 'nova-message nova-message--assistant';
      errorEl.setAttribute('aria-live', 'assertive');
      errorEl.textContent = this.i18n.t('error_fallback');
      this.messagesContainer.appendChild(errorEl);
      this.scrollToBottom();
    } finally {
      this.isSending = false;
      this.updateSendButton();
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Attachment previews                                              */
  /* ---------------------------------------------------------------- */

  private renderAttachmentPreviews(): void {
    // Remove existing preview container
    const existing = this.inputArea.querySelector('.nova-attachment-previews');
    if (existing) existing.remove();

    if (this.pendingAttachments.length > 0) {
      const previews = createAttachmentPreviews(
        this.pendingAttachments,
        (idx) => {
          this.pendingAttachments.splice(idx, 1);
          this.renderAttachmentPreviews();
          this.updateSendButton();
        },
      );
      // Insert before the first child (above the input row)
      this.inputArea.insertBefore(previews, this.inputArea.firstChild);
    }
  }

  private resetAttachmentCount(): void {
    const areaWithReset = this.inputArea as HTMLElement & {
      _novaResetAttachmentCount?: () => void;
    };
    if (areaWithReset._novaResetAttachmentCount) {
      areaWithReset._novaResetAttachmentCount();
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Issues tab                                                       */
  /* ---------------------------------------------------------------- */

  private async loadIssues(): Promise<void> {
    // Show loading skeleton in the accordion list
    this.issuesListEl.innerHTML = '';
    const loading = this.buildLoadingSkeleton();
    this.issuesListEl.appendChild(loading);

    try {
      const result = await this.api.getTickets();
      const tickets = result.data.items;

      this.issuesListEl.innerHTML = '';
      this.issueCount = tickets.length;
      this.issuesCountEl.textContent = String(this.issueCount);

      if (tickets.length === 0) {
        this.issuesListEl.appendChild(this.buildEmptyState());
        return;
      }

      for (const ticket of tickets) {
        this.issuesListEl.appendChild(this.buildIssueCard(ticket));
      }
    } catch {
      this.issuesListEl.innerHTML = '';
      const errorEl = document.createElement('div');
      errorEl.className = 'nova-empty-state';
      errorEl.setAttribute('aria-live', 'assertive');
      errorEl.textContent = this.i18n.t('error_fallback');
      this.issuesListEl.appendChild(errorEl);
    }
  }

  private buildIssueCard(ticket: UserTicket): HTMLElement {
    const card = document.createElement('div');
    card.className = 'nova-issue-card';
    card.setAttribute('role', 'article');
    card.setAttribute('aria-label', `${ticket.summary} — ${this.shortStatusLabel(ticket.status)}`);

    // Clickable header row
    const header = document.createElement('button');
    header.className = 'nova-issue-card__header';
    header.setAttribute('type', 'button');
    header.setAttribute('aria-expanded', 'false');

    // Status icon
    const iconWrap = document.createElement('span');
    iconWrap.className = 'nova-issue-card__icon';
    iconWrap.setAttribute('aria-hidden', 'true');
    iconWrap.innerHTML = this.issueStatusIcon(ticket.status);

    // Summary (visible in collapsed state)
    const summary = document.createElement('span');
    summary.className = 'nova-issue-card__summary';
    summary.textContent = ticket.summary;

    // Short status badge
    const badge = document.createElement('span');
    badge.className = `nova-issue-card__badge nova-issue-card__badge--${ticket.status}`;
    badge.textContent = this.shortStatusLabel(ticket.status);

    header.appendChild(iconWrap);
    header.appendChild(summary);
    header.appendChild(badge);

    // Expandable detail section
    const detail = document.createElement('div');
    detail.className = 'nova-issue-card__detail';

    // Status message (the verbose one from the API)
    if (ticket.statusLabel && ticket.statusLabel !== this.shortStatusLabel(ticket.status)) {
      const statusMsg = document.createElement('p');
      statusMsg.className = 'nova-issue-card__status-msg';
      statusMsg.textContent = ticket.statusLabel;
      detail.appendChild(statusMsg);
    }

    // Meta: type + category + date
    const meta = document.createElement('div');
    meta.className = 'nova-issue-card__meta';
    const parts: string[] = [];
    if (ticket.intent) parts.push(this.intentDisplayName(ticket.intent));
    if (ticket.category) parts.push(this.categoryDisplayName(ticket.category));
    parts.push(this.formatRelativeDate(ticket.createdAt));
    meta.textContent = parts.join(' · ');
    detail.appendChild(meta);

    // Follow-up button
    const followUpBtn = document.createElement('button');
    followUpBtn.className = 'nova-issue-card__follow-up';
    followUpBtn.setAttribute('type', 'button');
    followUpBtn.textContent = ticket.status === 'resolved' ? 'This is still an issue' : 'Add more details';
    followUpBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const prefix = ticket.status === 'resolved'
        ? `Re: "${ticket.summary}" — this issue isn't resolved. `
        : `Re: "${ticket.summary}" — `;
      this.textarea.value = prefix;
      this.textarea.focus();
      this.autoResizeTextarea();
    }, { signal: this.listenerAbort.signal });
    detail.appendChild(followUpBtn);

    card.appendChild(header);
    card.appendChild(detail);

    // Toggle expand/collapse
    header.addEventListener('click', () => {
      const expanded = header.getAttribute('aria-expanded') === 'true';
      header.setAttribute('aria-expanded', String(!expanded));
      card.classList.toggle('nova-issue-card--expanded');
    }, { signal: this.listenerAbort.signal });

    return card;
  }

  /** Short status label for badges (not the verbose API message). */
  private shortStatusLabel(status: string): string {
    const map: Record<string, string> = {
      received: 'Received',
      investigating: 'Investigating',
      triaging: 'Triaging',
      analysing: 'Analysing',
      in_progress: 'In Progress',
      executing: 'In Progress',
      resolved: 'Resolved',
      dismissed: 'Closed',
      archived: 'Archived',
    };
    return map[status] ?? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /** Format ISO date to relative string (e.g., "2 days ago"). */
  private formatRelativeDate(iso: string): string {
    const date = new Date(iso);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  /** Map intent to user-facing display name. */
  private intentDisplayName(intent: string): string {
    const map: Record<string, string> = {
      bug_report: 'Bug',
      feature_request: 'Feature',
      question: 'Question',
      status_check: 'Status',
      greeting: 'General',
    };
    return map[intent] ?? 'Issue';
  }

  /**
   * Return an SVG string for the issue row icon based on ticket status.
   * Resolved = checkmark, active statuses = gear, others = chat bubble.
   */
  private issueStatusIcon(status: string): string {
    if (status === 'resolved') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    }
    if (status === 'in_progress' || status === 'executing') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>';
    }
    // Default: chat bubble for investigating, triaging, received, etc.
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>';
  }

  private buildEmptyState(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'nova-empty-state';

    const icon = document.createElement('div');
    icon.className = 'nova-empty-state__icon';
    icon.textContent = '\u2714'; // checkmark
    icon.setAttribute('aria-hidden', 'true');

    const text = document.createElement('p');
    text.textContent = this.i18n.t('empty_issues');

    el.appendChild(icon);
    el.appendChild(text);

    return el;
  }

  private buildLoadingSkeleton(): HTMLElement {
    const skeleton = document.createElement('div');
    skeleton.className = 'nova-loading';
    skeleton.setAttribute('aria-label', 'Loading');
    skeleton.setAttribute('role', 'status');

    const widths = ['long', 'medium', 'short', 'long', 'medium'];
    for (const w of widths) {
      const bar = document.createElement('div');
      bar.className = `nova-loading__bar nova-loading__bar--${w}`;
      skeleton.appendChild(bar);
    }

    return skeleton;
  }

  /* ---------------------------------------------------------------- */
  /*  Unread badge polling                                             */
  /* ---------------------------------------------------------------- */

  private async checkUnread(): Promise<void> {
    try {
      const lastVisit = localStorage.getItem('nova-widget-last-visit') ?? new Date(0).toISOString();
      const result = await this.api.getTicketUpdates(lastVisit);
      this.updateTriggerBadge(result.data.items.length > 0);
    } catch {
      // Silently fail — badge is a nice-to-have
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Updates banner                                                   */
  /* ---------------------------------------------------------------- */

  private async checkUpdates(): Promise<void> {
    try {
      const lastSeen = this.getLastSeen();
      const result = await this.api.getTicketUpdates(lastSeen);
      const updates = result.data.items;

      if (updates.length === 0) {
        this.updatesBanner.style.display = 'none';
        return;
      }

      // Check for resolved updates that need feedback
      const resolvedUpdates = updates.filter(
        (u) => u.newStatus === 'resolved' && u.userFeedback === null,
      );

      this.updatesBanner.innerHTML = '';
      this.updatesBanner.style.display = 'flex';

      if (resolvedUpdates.length > 0) {
        this.buildFeedbackBanner(resolvedUpdates[0]);
      } else {
        const iconSpan = document.createElement('span');
        iconSpan.className = 'nova-updates-banner__icon';
        iconSpan.textContent = '\u2139'; // info symbol
        iconSpan.setAttribute('aria-hidden', 'true');

        const textSpan = document.createElement('span');
        textSpan.textContent = `${updates.length} update(s) ${this.i18n.t('updates_banner').toLowerCase()}`;

        this.updatesBanner.appendChild(iconSpan);
        this.updatesBanner.appendChild(textSpan);
      }

      // Update badge on trigger
      this.updateTriggerBadge(true);

      // Store current time
      this.setLastSeen();
    } catch {
      // Silently fail — banner just stays hidden
      this.updatesBanner.style.display = 'none';
    }
  }

  private buildFeedbackBanner(update: TicketUpdate): void {
    this.updatesBanner.innerHTML = '';
    this.updatesBanner.style.flexDirection = 'column';
    this.updatesBanner.style.alignItems = 'flex-start';

    const text = document.createElement('span');
    text.textContent = update.userFacingMessage || 'Did this fix your issue?';
    this.updatesBanner.appendChild(text);

    const feedbackRow = document.createElement('div');
    feedbackRow.className = 'nova-feedback';

    const yesBtn = document.createElement('button');
    yesBtn.className = 'nova-feedback__btn nova-feedback__btn--positive';
    yesBtn.setAttribute('type', 'button');
    yesBtn.textContent = this.i18n.t('feedback_yes');
    yesBtn.addEventListener('click', () => {
      void this.submitFeedback(update.ticketId, 'confirmed_resolved');
    }, { signal: this.listenerAbort.signal });

    const noBtn = document.createElement('button');
    noBtn.className = 'nova-feedback__btn nova-feedback__btn--negative';
    noBtn.setAttribute('type', 'button');
    noBtn.textContent = this.i18n.t('feedback_no');
    noBtn.addEventListener('click', () => {
      void this.submitFeedback(update.ticketId, 'still_broken');
    }, { signal: this.listenerAbort.signal });

    feedbackRow.appendChild(yesBtn);
    feedbackRow.appendChild(noBtn);
    this.updatesBanner.appendChild(feedbackRow);
  }

  private async submitFeedback(
    ticketId: string,
    feedback: 'confirmed_resolved' | 'still_broken',
  ): Promise<void> {
    try {
      await this.api.submitFeedback(ticketId, { feedback });
      this.updatesBanner.innerHTML = '';

      const msg = document.createElement('span');
      msg.textContent =
        feedback === 'confirmed_resolved'
          ? this.i18n.t('feedback_thanks')
          : this.i18n.t('feedback_reopened');
      this.updatesBanner.appendChild(msg);

      // Hide banner after a few seconds
      setTimeout(() => {
        this.updatesBanner.style.display = 'none';
      }, 4000);

      this.emitEvent('feedback:submitted', { ticketId, feedback });
    } catch {
      // Silently fail
    }
  }

  /* ---------------------------------------------------------------- */
  /*  localStorage helpers                                             */
  /* ---------------------------------------------------------------- */

  private getLastSeen(): string {
    try {
      return localStorage.getItem(STORAGE_LAST_SEEN) ?? new Date(0).toISOString();
    } catch {
      return new Date(0).toISOString();
    }
  }

  private setLastSeen(): void {
    try {
      localStorage.setItem(STORAGE_LAST_SEEN, new Date().toISOString());
    } catch {
      // Silently ignore
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Trigger badge                                                    */
  /* ---------------------------------------------------------------- */

  private updateTriggerBadge(hasUpdates: boolean): void {
    if (!this.triggerElement) return;
    if (hasUpdates) {
      this.triggerElement.classList.add('nova-badge-dot');
    } else {
      this.triggerElement.classList.remove('nova-badge-dot');
    }
    if (this.badgeEl) {
      this.badgeEl.style.display = hasUpdates ? 'block' : 'none';
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Keyboard handling                                                */
  /* ---------------------------------------------------------------- */

  private handleKeydown(e: KeyboardEvent): void {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }

    // Focus trap: Tab cycles within the panel
    if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  }

  private handleTextareaKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void this.handleSend();
    }
  }

  private trapFocus(e: KeyboardEvent): void {
    const focusableSelector =
      'button:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(
      this.panel.querySelectorAll(focusableSelector),
    ) as HTMLElement[];

    if (focusableElements.length === 0) return;

    const firstEl = focusableElements[0];
    const lastEl = focusableElements[focusableElements.length - 1];

    // Find which element in the shadow DOM has focus
    const activeEl = this.shadow.activeElement as HTMLElement | null;

    if (e.shiftKey) {
      // Shift+Tab: if on first element, wrap to last
      if (activeEl === firstEl || !activeEl) {
        e.preventDefault();
        lastEl.focus();
      }
    } else {
      // Tab: if on last element, wrap to first
      if (activeEl === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Textarea auto-resize                                             */
  /* ---------------------------------------------------------------- */

  private autoResizeTextarea(): void {
    this.textarea.style.height = 'auto';
    this.textarea.style.height = `${Math.min(this.textarea.scrollHeight, 120)}px`;
    this.updateSendButton();
  }

  private updateSendButton(): void {
    const hasText = this.textarea.value.trim().length > 0;
    const hasAttachments = this.pendingAttachments.length > 0;
    this.sendBtn.disabled = this.isSending || (!hasText && !hasAttachments);
  }

  /* ---------------------------------------------------------------- */
  /*  Host page design token reading                                   */
  /* ---------------------------------------------------------------- */

  /**
   * Read CSS custom properties from the host page and map them to widget
   * theme tokens. Supports shadcn/ui (bare HSL values), Tailwind, and
   * common naming conventions. Returns only tokens that were found — callers
   * merge with other sources (init config, server config, CSS defaults).
   */
  private readHostTokens(): Partial<WidgetTheme> {
    if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') return {};

    const rootStyle = getComputedStyle(document.documentElement);
    const bodyStyle = document.body ? getComputedStyle(document.body) : null;

    const get = (name: string): string => {
      const val = rootStyle.getPropertyValue(name).trim();
      if (val) return val;
      return bodyStyle?.getPropertyValue(name).trim() ?? '';
    };

    // Convert shadcn-style bare HSL value ("142 76% 36%") to hsl() string
    const toColor = (val: string): string | null => {
      if (!val) return null;
      if (val.startsWith('#') || val.startsWith('rgb') || val.startsWith('hsl(')) return val;
      if (/^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/.test(val)) return `hsl(${val})`;
      return null;
    };

    const tokens: Partial<WidgetTheme> = {};

    // Font: CSS custom properties → computed body font
    const fontVar = get('--font-sans') || get('--font-family') || get('--font-body');
    if (fontVar) {
      tokens.fontFamily = fontVar;
    } else if (bodyStyle) {
      const bodyFont = bodyStyle.fontFamily;
      if (bodyFont && !/\bTimes\b/.test(bodyFont) && bodyFont !== 'serif') {
        tokens.fontFamily = bodyFont;
      }
    }

    // Border radius
    const radius = get('--radius');
    if (radius) tokens.borderRadius = radius;

    // Brand / primary color (shadcn → generic → named)
    const primary = toColor(get('--primary'));
    if (primary) {
      tokens.brandColor = primary;
    } else {
      const alt = get('--color-primary') || get('--brand-color') || get('--accent-color');
      if (alt) tokens.brandColor = alt;
    }

    // Background and foreground are intentionally NOT read from the host page.
    // Host page bg/fg tokens (--background, --foreground) are designed for the
    // page itself, not for an overlay widget. Using them produces colour clashes
    // (e.g. dark sidebar bg leaking into widget bubbles). Instead, derivePalette
    // generates a cohesive light/dark palette from the brand colour alone.

    return tokens;
  }

  /* ---------------------------------------------------------------- */
  /*  Theme helpers                                                    */
  /* ---------------------------------------------------------------- */

  /**
   * Detect dark mode from the host page's DOM state.
   * Checks common conventions: Tailwind `dark` class on `<html>`, `data-theme`
   * attribute, `data-color-scheme` attribute. Falls back to `prefers-color-scheme`.
   */
  private detectHostDarkMode(): boolean {
    if (typeof document === 'undefined') return false;
    const html = document.documentElement;

    // Tailwind / common convention: class="dark" or "light" on <html>
    if (html.classList.contains('dark')) return true;
    if (html.classList.contains('light')) return false;

    // data-theme / data-color-scheme / data-mode (shadcn, daisyUI, next-themes, etc.)
    const theme = html.getAttribute('data-theme')
      ?? html.getAttribute('data-color-scheme')
      ?? html.getAttribute('data-mode');
    if (theme === 'dark') return true;
    if (theme === 'light') return false;

    // color-scheme CSS property on <html> (next-themes sets this)
    const colorScheme = html.style.colorScheme;
    if (colorScheme === 'dark') return true;
    if (colorScheme === 'light') return false;

    // Check body as well
    if (document.body?.classList.contains('dark')) return true;
    if (document.body?.classList.contains('light')) return false;

    const bodyTheme = document.body?.getAttribute('data-theme')
      ?? document.body?.getAttribute('data-color-scheme')
      ?? document.body?.getAttribute('data-mode');
    if (bodyTheme === 'dark') return true;
    if (bodyTheme === 'light') return false;

    if (document.body?.style.colorScheme === 'dark') return true;
    if (document.body?.style.colorScheme === 'light') return false;

    // No explicit signal from host page — fall back to OS preference
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  }

  /**
   * Watch the host page's `<html>` and `<body>` for dark mode class / attribute
   * changes, and re-derive the palette + rebuild styles when they change.
   */
  private watchHostDarkMode(): void {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;

    let lastDark = this.detectHostDarkMode();

    const handleChange = (): void => {
      const nowDark = this.detectHostDarkMode();
      if (nowDark === lastDark) return;
      lastDark = nowDark;

      // Rebuild styles with the correct dark mode
      const newStyles = getWidgetStyles({ darkMode: nowDark });
      const newStyleEl = createStyleElement(newStyles);
      this.shadow.replaceChild(newStyleEl, this.styleEl);
      this.styleEl = newStyleEl;

      // Re-read host tokens (colors/fonts may change with theme switch)
      const freshTokens = this.readHostTokens();
      if (Object.keys(freshTokens).length > 0) {
        this.applyTheme(freshTokens);
      }

      // Re-derive palette-based CSS custom properties if we have a brand color
      const brandColor = this.host.style.getPropertyValue('--nova-theme-brand')?.trim();
      if (brandColor) {
        const derived = this.derivePalette(brandColor);
        this.host.style.setProperty('--nova-theme-bg', derived.bg);
        this.host.style.setProperty('--nova-theme-bg-secondary', derived.bgSecondary);
        this.host.style.setProperty('--nova-theme-text', derived.text);
        this.host.style.setProperty('--nova-theme-text-secondary', derived.textSecondary);
      }
    };

    // Watch class / data-attribute changes on <html> and <body>
    this.darkModeObserver = new MutationObserver(handleChange);
    this.darkModeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'data-color-scheme', 'data-mode', 'style'],
    });
    if (document.body) {
      this.darkModeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'data-color-scheme', 'data-mode', 'style'],
      });
    }

    // Watch OS-level prefers-color-scheme media query changes
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.darkModeMediaHandler = () => handleChange();
      this.darkModeMediaQuery.addEventListener('change', this.darkModeMediaHandler);
    }
  }

  /**
   * Derive a cohesive light/dark palette from a single brand colour.
   *
   * Two key techniques prevent bad output:
   *
   * 1. **Desaturation** — High-saturation brands (reds, purples) are
   *    desaturated toward their own luminance gray before tinting.
   *    This preserves warm/cool temperature without introducing strong
   *    chroma (e.g. red brand → warm neutral bg, not pink bg).
   *
   * 2. **Adaptive boost** — Low-saturation and extreme-lightness brands
   *    get a modest tint-strength boost so the brand character is
   *    perceptible. Capped to avoid over-tinting.
   */
  private derivePalette(brandColor: string): {
    bg: string;
    bgSecondary: string;
    text: string;
    textSecondary: string;
  } {
    // Normalize any CSS color format to #RRGGBB hex before processing.
    // Falls back to neutral gray if the input cannot be parsed.
    const brandHex = this.toHex(brandColor) ?? '#6b7280';
    const { h, s, l } = this.hexToHsl(brandHex);
    const prefersDark = this.detectHostDarkMode();

    const isWarm = h >= 330 || h <= 80;

    // --- Adaptive boost for low-sat / extreme-lightness brands ---
    const satBoost = s < 15 ? 1.6 : s < 35 ? 1.25 : 1.0;
    const lBoost = prefersDark
      ? (l > 65 ? 1.3 : 1.0)
      : (l < 25 ? 1.3 : 1.0);
    const boost = Math.min(satBoost * lBoost, 2.0);

    // --- Desaturate brand toward its luminance gray ---
    // Prevents chromatic tints (pink from red, lavender from purple).
    // Low-sat brands skip this (already gray-ish).
    const r = parseInt(brandHex.slice(1, 3), 16);
    const g = parseInt(brandHex.slice(3, 5), 16);
    const b = parseInt(brandHex.slice(5, 7), 16);
    const gray = (r + g + b) / 3;
    const desat = s > 50 ? 0.5 : s > 30 ? 0.25 : 0;
    const tr = r + (gray - r) * desat;
    const tg = g + (gray - g) * desat;
    const tb = b + (gray - b) * desat;

    const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));

    if (prefersDark) {
      const t1 = 0.07 * boost;
      const t2 = 0.11 * boost;
      return {
        bg: `rgb(${clamp(tr * t1 + 17)}, ${clamp(tg * t1 + 17)}, ${clamp(tb * t1 + 19)})`,
        bgSecondary: `rgb(${clamp(tr * t2 + 25)}, ${clamp(tg * t2 + 25)}, ${clamp(tb * t2 + 27)})`,
        text: '#ebebeb',
        textSecondary: '#a0a0a0',
      };
    }

    const t1 = 0.035 * boost;
    const t2 = 0.065 * boost;
    return {
      bg: `rgb(${clamp(255 - (255 - tr) * t1)}, ${clamp(255 - (255 - tg) * t1)}, ${clamp(255 - (255 - tb) * t1)})`,
      bgSecondary: `rgb(${clamp(255 - (255 - tr) * t2)}, ${clamp(255 - (255 - tg) * t2)}, ${clamp(255 - (255 - tb) * t2)})`,
      text: '#1a1a1a',
      textSecondary: isWarm ? '#5c5550' : '#505560',
    };
  }

  /**
   * Normalize any CSS color string to #RRGGBB hex.
   * Handles: #RRGGBB, #RGB, hsl(...), rgb(...), bare HSL ("221 83% 53%").
   * Returns null if the input cannot be parsed.
   */
  private toHex(color: string): string | null {
    const c = color.trim();
    // Already #RRGGBB
    if (/^#[0-9a-fA-F]{6}$/.test(c)) return c;
    // #RGB shorthand → #RRGGBB
    if (/^#[0-9a-fA-F]{3}$/.test(c)) {
      return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    }
    // Bare HSL value from shadcn: "221 83% 53%"
    const bareHsl = c.match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
    if (bareHsl) {
      return this.hslToHex(parseFloat(bareHsl[1]), parseFloat(bareHsl[2]), parseFloat(bareHsl[3]));
    }
    // hsl(H, S%, L%) or hsl(H S% L%) — with or without commas/deg suffix
    const hslMatch = c.match(/^hsl\(\s*(\d+(?:\.\d+)?)\s*(?:deg)?\s*[,\s]\s*(\d+(?:\.\d+)?)%\s*[,\s]\s*(\d+(?:\.\d+)?)%\s*\)$/i);
    if (hslMatch) {
      return this.hslToHex(parseFloat(hslMatch[1]), parseFloat(hslMatch[2]), parseFloat(hslMatch[3]));
    }
    // rgb(R, G, B) or rgb(R G B)
    const rgbMatch = c.match(/^rgb\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)\s*\)$/i);
    if (rgbMatch) {
      const toHexPart = (v: number) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
      return `#${toHexPart(parseInt(rgbMatch[1]))}${toHexPart(parseInt(rgbMatch[2]))}${toHexPart(parseInt(rgbMatch[3]))}`;
    }
    return null;
  }

  /** Convert HSL values to #RRGGBB hex. */
  private hslToHex(h: number, s: number, l: number): string {
    const sn = s / 100;
    const ln = l / 100;
    const a = sn * Math.min(ln, 1 - ln);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = ln - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * Math.max(0, Math.min(1, color))).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  /** Convert hex (#RRGGBB) to HSL. */
  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) return { h: 0, s: 0, l: l * 100 };

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    let h = 0;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;

    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  /* ---------------------------------------------------------------- */
  /*  Theme application                                                */
  /* ---------------------------------------------------------------- */

  /**
   * Apply theme tokens to the shadow host via CSS custom properties.
   * When only brandColor is provided, derives a full palette.
   */
  private applyTheme(theme: WidgetTheme): void {
    if (theme.brandColor) {
      this.host.style.setProperty('--nova-theme-brand', theme.brandColor);

      if (!theme.backgroundColor || !theme.textColor) {
        const derived = this.derivePalette(theme.brandColor);
        if (!theme.backgroundColor) {
          this.host.style.setProperty('--nova-theme-bg', derived.bg);
          this.host.style.setProperty(
            '--nova-theme-bg-secondary',
            derived.bgSecondary,
          );
        }
        if (!theme.textColor) {
          this.host.style.setProperty('--nova-theme-text', derived.text);
          this.host.style.setProperty(
            '--nova-theme-text-secondary',
            derived.textSecondary,
          );
        }
      }
    }
    if (theme.backgroundColor) {
      this.host.style.setProperty('--nova-theme-bg', theme.backgroundColor);
    }
    if (theme.textColor) {
      this.host.style.setProperty('--nova-theme-text', theme.textColor);
    }
    if (theme.fontFamily) {
      this.host.style.setProperty('--nova-theme-font', theme.fontFamily);
    }
    if (theme.borderRadius) {
      this.host.style.setProperty('--nova-theme-radius', theme.borderRadius);
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Config loading                                                   */
  /* ---------------------------------------------------------------- */

  private async loadConfig(): Promise<void> {
    try {
      const result = await this.api.getConfig();
      this.chatbotConfig = result.data;

      // Apply display name
      const titleEl = this.panel.querySelector('.nova-header__title');
      if (titleEl) {
        titleEl.textContent = this.chatbotConfig.appDisplayName;
      }

      // Merge priority: init theme > host page tokens > server config > CSS defaults.
      const initTheme = this.config.theme;
      const hostTk = this.readHostTokens();
      const serverTheme = this.chatbotConfig.theme;
      const merged: WidgetTheme = {
        brandColor: initTheme?.brandColor || hostTk.brandColor || serverTheme.brandColor,
        backgroundColor: initTheme?.backgroundColor || hostTk.backgroundColor || serverTheme.backgroundColor || undefined,
        textColor: initTheme?.textColor || hostTk.textColor || serverTheme.textColor || undefined,
        fontFamily: initTheme?.fontFamily || hostTk.fontFamily || serverTheme.fontFamily,
        borderRadius: initTheme?.borderRadius || hostTk.borderRadius || serverTheme.borderRadius,
      };
      this.applyTheme(merged);

      // Re-apply dark mode from server config (init theme takes precedence).
      // For 'auto', detect from host page rather than relying on prefers-color-scheme.
      const effectiveDarkMode = initTheme?.darkMode ?? serverTheme.darkMode ?? 'auto';
      const resolvedDark = effectiveDarkMode === 'auto'
        ? this.detectHostDarkMode()
        : effectiveDarkMode;
      const newStyles = getWidgetStyles({ darkMode: resolvedDark });
      const newStyleEl = createStyleElement(newStyles);
      this.shadow.replaceChild(newStyleEl, this.styleEl);
      this.styleEl = newStyleEl;

      // Apply position (left/right) from server config
      if (this.chatbotConfig.position === 'left') {
        this.panel.style.left = '0';
        this.panel.style.right = 'auto';
        this.panel.style.transform = this.panel.classList.contains('nova-panel--open')
          ? 'translateX(0)' : 'translateX(-100%)';
        this.panel.style.borderRadius =
          `0 var(--nova-theme-radius, 8px) var(--nova-theme-radius, 8px) 0`;
      }

      // Apply custom width from server config
      if (this.chatbotConfig.widgetWidth && this.chatbotConfig.widgetWidth !== 420) {
        this.panel.style.width = `${this.chatbotConfig.widgetWidth}px`;
      }

      // Apply i18n overrides
      if (
        this.chatbotConfig.i18nOverrides &&
        Object.keys(this.chatbotConfig.i18nOverrides).length > 0
      ) {
        this.i18n = new I18n(
          this.chatbotConfig.locale,
          this.chatbotConfig.i18nOverrides,
        );
      }

      // Update welcome message if customized
      if (
        this.chatbotConfig.welcomeMessage &&
        this.messagesContainer.firstChild
      ) {
        const firstMsg = this.messagesContainer.firstChild as HTMLElement;
        firstMsg.textContent = this.chatbotConfig.welcomeMessage;
        // Also update in messages array
        if (this.messages[0]) {
          this.messages[0].content = this.chatbotConfig.welcomeMessage;
        }
      }

      // Render topic chips below the welcome message.
      // Use suggestedPrompts if available, otherwise generate conversational
      // prompts from categories (not just display names — those are useless).
      const topicChips = this.chatbotConfig.suggestedPrompts?.length
        ? this.chatbotConfig.suggestedPrompts
        : (this.chatbotConfig.categories ?? []).map((cat) => this.categoryToPrompt(cat));
      this.renderSuggestedPrompts(topicChips);
    } catch {
      // Non-critical — widget works with defaults
    }

    // Start unread badge polling (30s interval)
    this.unreadInterval = setInterval(() => void this.checkUnread(), 30_000);
    void this.checkUnread();
  }

  /* ---------------------------------------------------------------- */
  /*  Event emission                                                   */
  /* ---------------------------------------------------------------- */

  private emitEvent(event: string, data: unknown): void {
    if (this.config.emitter) {
      this.config.emitter.emit(event, data);
    }
  }

  /* ---------------------------------------------------------------- */
  /*  SVG icons (inline to avoid external deps)                        */
  /* ---------------------------------------------------------------- */

  private sendSvg(): string {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>';
  }

  private paperclipSvg(): string {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>';
  }

  /** Premium sparkle icon for the AI Assistant header. */
  private novaSparklesSvg(): string {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"></path><path d="M19 2l.5 1.5L21 4l-1.5.5L19 6l-.5-1.5L17 4l1.5-.5L19 2z" stroke-width="1.25"></path></svg>';
  }

  private clipboardSvg(): string {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path></svg>';
  }

  /* ---------------------------------------------------------------- */
  /*  Category display name mapping                                    */
  /* ---------------------------------------------------------------- */

  private categoryDisplayName(category: string): string {
    const map: Record<string, string> = {
      bug: 'Bug Reports',
      bug_report: 'Bug Reports',
      feature_request: 'Feature Requests',
      question: 'Questions',
      feedback: 'Feedback',
      support: 'Support',
    };
    return map[category] ?? category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /** Map a category to a conversational prompt for topic chips. */
  private categoryToPrompt(category: string): string {
    const map: Record<string, string> = {
      bug: 'I found a bug',
      bug_report: 'I found a bug',
      feature_request: 'I have a feature idea',
      question: 'I have a question',
      feedback: 'I have some feedback',
      support: 'I need help with something',
    };
    return map[category] ?? `I need help with ${category.replace(/_/g, ' ')}`;
  }
}
